import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AttachFile,
  Check,
  DeleteOutline,
  Movie,
  PictureAsPdf,
  TaskAlt,
  YouTube,
} from "@mui/icons-material";
import {
  useCreateNoteMutation,
  useUpdateNoteMutation,
} from "../../../../../app/notesApiSlice";
import { useGetTeacherAssignedSubjectsQuery } from "../../../../../app/teacherApiSlice";
import { useCreateResourceMutation } from "../../../../../app/resourceApiSlice";
import { StyledTextField } from "../../../../../customComponents/styled/styledInputs";
import { FilledButton } from "../../../../../customComponents/styled/styledButtons";
import CustomTipTapEditor from "../../../../../customComponents/CustomTipTapEditor";
import "./EditorStyles.css";

const NewNote = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const cardData = state?.cardData || null;

  const [createNote, { isLoading: isCreatingNote }] = useCreateNoteMutation();
  const [updateNote, { isLoading: isUpdatingNote }] = useUpdateNoteMutation();
  const [createResource, { isLoading: isUploadingResource }] = useCreateResourceMutation();
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");

  // Fetch teacher's assigned subjects and classrooms
  const { data: teacherAssignedClasses = [] } = useGetTeacherAssignedSubjectsQuery();

  // Extract unique subjects from the assigned classes data
  const availableSubjects = useMemo(() => {
    if (!teacherAssignedClasses || !Array.isArray(teacherAssignedClasses)) return [];
    const uniqueSubjects = {};
    teacherAssignedClasses.forEach((classroom) => {
      if (classroom && classroom.name && !uniqueSubjects[classroom.name]) {
        uniqueSubjects[classroom.name] = {
          name: classroom.name,
          description: classroom.description,
        };
      }
    });
    return Object.values(uniqueSubjects);
  }, [teacherAssignedClasses]);

  const formik = useFormik({
    initialValues: {
      subject: cardData?.subject || "",
      level: cardData?.level || "",
      topic: cardData?.topic || "",
      body: cardData?.body || "",
      resources: cardData?.resources || [],
    },
    validationSchema: yup.object({
      subject: yup.string().required("Subject is required"),
      level: yup.string().required("Level is required"),
      topic: yup.string().required("Topic is required"),
      body: yup.string().required("Content is required"),
    }),
    onSubmit: async (values) => {
      try {
        const resourceIds = values.resources.map((resource) => resource.id);
        if (cardData?.id) {
          await updateNote({
            id: cardData.id,
            body: {
              subject: values.subject,
              topic: values.topic,
              level: values.level,
              body: values.body,
              resources: resourceIds,
            },
          }).unwrap();
        } else {
          await createNote({
            ...values,
            resources: resourceIds,
          }).unwrap();
        }
        formik.resetForm();
        setOpenSuccessModal(true);
        setTimeout(() => {
          navigate("/lms/notes");
        }, 1500);
      } catch (error) {
        console.error("Error:", error);
      }
    },
  });

  const submitRef = useRef(null);
  useEffect(() => { submitRef.current = formik.handleSubmit; });
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        submitRef.current?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Get available levels for the selected subject
  const availableLevels = useMemo(() => {
    if (!formik.values.subject || !teacherAssignedClasses || !Array.isArray(teacherAssignedClasses)) {
      return [];
    }
    const levels = new Set();
    teacherAssignedClasses.forEach((classroom) => {
      if (classroom && classroom.name === formik.values.subject && classroom.level) {
        levels.add(classroom.level);
      }
    });
    return Array.from(levels).sort();
  }, [formik.values.subject, teacherAssignedClasses]);

  const handleAttachmentUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    setAttachmentError("");

    const validFiles = files.filter((file) => (
      file.type === "application/pdf" || file.type.startsWith("video/")
    ));

    if (validFiles.length !== files.length) {
      setAttachmentError("Only PDF and video files can be attached to notes.");
    }

    if (!validFiles.length) return;

    const formData = new FormData();
    validFiles.forEach((file) => formData.append("file", file));

    try {
      const response = await createResource(formData).unwrap();
      const uploadedResources = response?.resources || [];
      formik.setFieldValue("resources", [
        ...formik.values.resources,
        ...uploadedResources,
      ]);
    } catch (error) {
      console.error("Attachment upload failed:", error);
      setAttachmentError(error?.data?.message || "Attachment upload failed. Please try again.");
    }
  };

  const handleRemoveAttachment = (resourceId) => {
    formik.setFieldValue(
      "resources",
      formik.values.resources.filter((resource) => resource.id !== resourceId)
    );
  };

  const isYouTubeUrl = (url) => {
    try {
      const parsedUrl = new URL(url);
      const host = parsedUrl.hostname.replace(/^www\./, "");
      if (host === "youtu.be") {
        return Boolean(parsedUrl.pathname.split("/").filter(Boolean)[0]);
      }
      if (!["youtube.com", "m.youtube.com"].includes(host)) {
        return false;
      }
      if (parsedUrl.pathname.startsWith("/embed/") || parsedUrl.pathname.startsWith("/shorts/")) {
        return Boolean(parsedUrl.pathname.split("/").filter(Boolean)[1]);
      }
      return Boolean(parsedUrl.searchParams.get("v"));
    } catch {
      return false;
    }
  };

  const handleAddYouTubeLink = async () => {
    const trimmedUrl = youtubeUrl.trim();
    setAttachmentError("");

    if (!isYouTubeUrl(trimmedUrl)) {
      setAttachmentError("Enter a valid YouTube link.");
      return;
    }

    try {
      const response = await createResource({
        name: "YouTube video",
        type: "youtube",
        url: trimmedUrl,
      }).unwrap();
      const resources = response?.resources || [];
      formik.setFieldValue("resources", [
        ...formik.values.resources,
        ...resources,
      ]);
      setYoutubeUrl("");
    } catch (error) {
      console.error("YouTube attachment failed:", error);
      setAttachmentError(error?.data?.message || "YouTube link could not be attached. Please try again.");
    }
  };

  const getAttachmentLabel = (type) => {
    if (type === "pdf") return "PDF";
    if (type === "youtube") return "YouTube";
    return "Video";
  };

  const StepBadge = ({ number }) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", backgroundColor: "primary.main", color: "primary.contrastText", fontSize: "14px", fontWeight: 600 }}>
      {number}
    </Box>
  );

  const isSubmitting = isCreatingNote || isUpdatingNote;
  const attachmentInputId = "note-attachment-upload";

  return (
    <Box sx={{ p: 2 }}>
      <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: "100%" }}>
        <Typography sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, fontWeight: 600, mb: { xs: 2, md: 4 } }}>
          {cardData?.id ? "Update Note" : "Create New Note"}
        </Typography>

        <Dialog
          fullWidth
          open={openSuccessModal}
          onClose={() => setOpenSuccessModal(false)}
          sx={{ "& .MuiPaper-root": { borderRadius: "24px", maxWidth: "776px" } }}
        >
          <Stack sx={{ alignItems: "center", gap: 2.5, py: 5, px: 3 }}>
            <TaskAlt sx={{ width: "136px", height: "162px", color: "primary.main" }} />
            <Typography sx={{ color: "text.primary", fontSize: "20px", fontWeight: 600 }}>
              {cardData?.id ? "Note Updated" : "Note Created"}
            </Typography>
            <Typography sx={{ color: "#95969D", fontSize: "16px", textAlign: "center" }}>
              Your note has been {cardData?.id ? "updated" : "created"} successfully
            </Typography>
            <FilledButton
              onClick={() => setOpenSuccessModal(false)}
              sx={{ width: "240px", fontWeight: 400, fontSize: "14px" }}
            >
              OK
            </FilledButton>
          </Stack>
        </Dialog>

        <Card sx={{ mb: 4, border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}` }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <StepBadge number={1} />
              Subject, Level & Topic
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Subject</InputLabel>
                  <Select
                    name="subject"
                    value={formik.values.subject}
                    onChange={(e) => {
                      formik.setFieldValue("subject", e.target.value);
                      formik.setFieldValue("level", "");
                    }}
                    onBlur={formik.handleBlur}
                    label="Subject"
                    sx={{ backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9", borderRadius: "12px" }}
                  >
                    <MenuItem value="">
                      <em>Select a subject</em>
                    </MenuItem>
                    {availableSubjects.map((subject) => (
                      <MenuItem key={subject.name} value={subject.name}>
                        {subject.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.subject && formik.errors.subject && (
                    <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                      {formik.errors.subject}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Level</InputLabel>
                  <Select
                    name="level"
                    value={formik.values.level}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Level"
                    disabled={!formik.values.subject}
                    sx={{ backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9", borderRadius: "12px", textTransform: "capitalize" }}
                  >
                    <MenuItem value="">
                      <em>{formik.values.subject ? "Select a level" : "Select subject first"}</em>
                    </MenuItem>
                    {availableLevels.map((level) => (
                      <MenuItem key={level} value={level} sx={{ textTransform: "capitalize" }}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.level && formik.errors.level && (
                    <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                      {formik.errors.level}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <StyledTextField
                    name="topic"
                    value={formik.values.topic}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter topic"
                    label="Topic"
                    InputLabelProps={{ shrink: true }}
                  />
                  {formik.touched.topic && formik.errors.topic && (
                    <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                      {formik.errors.topic}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 4, border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}` }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <StepBadge number={2} />
              Note Content
            </Typography>
            <CustomTipTapEditor
              value={formik.values.body}
              onChange={(html) => formik.setFieldValue("body", html)}
              placeholder="Write note content here..."
              showToolbar={true}
              minHeight="300px"
            />
            {formik.touched.body && formik.errors.body && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
                {formik.errors.body}
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card sx={{ mb: 4, border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}` }}>
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2} sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                <StepBadge number={3} />
                Attachments
              </Typography>
              <Button
                component="label"
                htmlFor={attachmentInputId}
                variant="outlined"
                startIcon={isUploadingResource ? <CircularProgress size={18} /> : <AttachFile />}
                disabled={isUploadingResource}
                sx={{ textTransform: "none" }}
              >
                {isUploadingResource ? "Uploading..." : "Add PDF or Video"}
              </Button>
              <Box
                component="input"
                id={attachmentInputId}
                type="file"
                accept="application/pdf,video/*"
                multiple
                onChange={handleAttachmentUpload}
                sx={{ display: "none" }}
              />
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Attach PDF handouts, uploaded video lessons, or YouTube videos students should view with this note.
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <StyledTextField
                  label="YouTube Link"
                  placeholder="Paste a YouTube URL"
                  value={youtubeUrl}
                  onChange={(event) => setYoutubeUrl(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleAddYouTubeLink();
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<YouTube />}
                onClick={handleAddYouTubeLink}
                disabled={isUploadingResource || !youtubeUrl.trim()}
                sx={{ minWidth: { xs: "100%", md: 180 }, textTransform: "none" }}
              >
                Add YouTube
              </Button>
            </Stack>
            {isUploadingResource && <LinearProgress sx={{ borderRadius: 1, mb: 2 }} />}
            {attachmentError && (
              <Typography color="error" variant="caption" sx={{ display: "block", mb: 2 }}>
                {attachmentError}
              </Typography>
            )}

            {formik.values.resources.length === 0 ? (
              <Box sx={{ border: (theme) => `1px dashed ${theme.palette.mode === "dark" ? "#555" : "#D0D5DD"}`, borderRadius: "12px", p: 3, textAlign: "center", color: "text.secondary" }}>
                <Typography variant="body2">No attachments added</Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {formik.values.resources.map((resource) => (
                  <Box
                    key={resource.id || resource.url}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      p: 2,
                      border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}`,
                      borderRadius: "12px",
                      backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                      {resource.type === "pdf" ? <PictureAsPdf color="error" /> : resource.type === "youtube" ? <YouTube color="error" /> : <Movie color="primary" />}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {resource.name || "Attachment"}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={getAttachmentLabel(resource.type)}
                          sx={{ mt: 0.75 }}
                        />
                      </Box>
                    </Stack>
                    <Tooltip title="Remove attachment">
                      <IconButton onClick={() => handleRemoveAttachment(resource.id)} color="error" size="small">
                        <DeleteOutline />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" sx={{ mt: 6, mb: 4 }}>
          <Button variant="outlined" onClick={() => navigate("/lms/notes")} sx={{ minWidth: 150 }} disabled={isSubmitting || isUploadingResource}>
            Cancel
          </Button>
          <FilledButton
            type="submit"
            disabled={isSubmitting || isUploadingResource}
            sx={{ minWidth: 200, fontWeight: 500, fontSize: "16px" }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              <>
                <Check sx={{ mr: 1 }} />
                {cardData?.id ? "Update Note" : "Create Note"}
              </>
            )}
          </FilledButton>
        </Stack>
      </Box>
    </Box>
  );
};

export default NewNote;
